import CloseIcon from '@mui/icons-material/Close'
import { Alert, IconButton, Snackbar, SnackbarCloseReason } from '@mui/material'
import {
  ReactNode,
  SyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'

type Severity = 'success' | 'error' | 'warning' | 'info'

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: Severity) => void
}

const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: () => {},
})

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [severity, setSeverity] = useState<Severity>('warning')

  const showSnackbar = useCallback((msg: string, sev: Severity = 'warning') => {
    setMessage(msg)
    setSeverity(sev)
    setOpen(true)
  }, [])

  const handleClose = (
    _: Event | SyntheticEvent,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return
    }
    setOpen(false)
  }

  const handleButtonClose = () => {
    setOpen(false)
  }

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={severity}
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              sx={{ color: 'white' }}
              onClick={handleButtonClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = () => useContext(SnackbarContext)
